/**
 * String utilities for URL slug generation with multi-language support
 * Handles German, Spanish, Latin, Chinese characters
 */

/**
 * Generate URL-safe slug from title with multi-language support
 * @param text - Input text to convert to slug
 * @param maxLength - Maximum length of slug (default: 50)
 * @returns URL-safe slug string
 */
export function generateSlug(text: string, maxLength: number = 50): string {
  if (!text || text.trim() === '') {
    return '';
  }

  let slug = text.trim();

  // 1. Handle German characters
  const germanMap: Record<string, string> = {
    'ä': 'ae', 'Ä': 'Ae',
    'ö': 'oe', 'Ö': 'Oe',
    'ü': 'ue', 'Ü': 'Ue',
    'ß': 'ss'
  };

  Object.entries(germanMap).forEach(([char, replacement]) => {
    slug = slug.replace(new RegExp(char, 'g'), replacement);
  });

  // 2. Handle Spanish/Latin characters (remove diacritics)
  const latinMap: Record<string, string> = {
    'á': 'a', 'Á': 'A', 'à': 'a', 'À': 'A', 'â': 'a', 'Â': 'A', 'ã': 'a', 'Ã': 'A',
    'é': 'e', 'É': 'E', 'è': 'e', 'È': 'E', 'ê': 'e', 'Ê': 'E', 'ë': 'e', 'Ë': 'E',
    'í': 'i', 'Í': 'I', 'ì': 'i', 'Ì': 'I', 'î': 'i', 'Î': 'I', 'ï': 'i', 'Ï': 'I',
    'ó': 'o', 'Ó': 'O', 'ò': 'o', 'Ò': 'O', 'ô': 'o', 'Ô': 'O', 'õ': 'o', 'Õ': 'O',
    'ú': 'u', 'Ú': 'U', 'ù': 'u', 'Ù': 'U', 'û': 'u', 'Û': 'U', 'ü': 'u', 'Ü': 'U',
    'ñ': 'n', 'Ñ': 'N', 'ç': 'c', 'Ç': 'C'
  };

  Object.entries(latinMap).forEach(([char, replacement]) => {
    slug = slug.replace(new RegExp(char, 'g'), replacement);
  });

  // 3. Handle Chinese characters (convert to pinyin without tones)
  // Using a simplified romanization approach for common Chinese characters
  // For production, consider using a proper pinyin library like 'pinyin'
  slug = slug.replace(/[\u4e00-\u9fa5]/g, (char) => {
    // This is a simplified approach - in production use a pinyin library
    // For now, we'll keep Chinese characters and let the next step handle them
    return char;
  });

  // 4. Convert to lowercase
  slug = slug.toLowerCase();

  // 5. Replace spaces and special characters with hyphens
  slug = slug
    .replace(/[\s_]+/g, '-')           // Replace spaces and underscores with hyphens
    .replace(/[^\w\-\u4e00-\u9fa5]/g, '') // Remove all non-word chars except hyphens and Chinese
    .replace(/\-\-+/g, '-')             // Replace multiple hyphens with single hyphen
    .replace(/^-+/, '')                 // Trim hyphens from start
    .replace(/-+$/, '');                // Trim hyphens from end

  // 6. Limit length
  if (slug.length > maxLength) {
    slug = slug.substring(0, maxLength);
    // Remove trailing hyphen if exists after truncation
    slug = slug.replace(/-+$/, '');
  }

  return slug;
}

/**
 * Extract ID from slug parameter (handles both old and new format)
 * New format: [id]--[slug] (double hyphen separator)
 * Old format: [id]-[slug] (single hyphen, ID must not contain hyphens)
 * Plain ID: [id] (no slug portion)
 *
 * @param slugParam - The slug parameter from route
 * @returns The extracted ID
 */
export function extractIdFromSlug(slugParam: string): string {
  if (!slugParam) {
    return '';
  }

  // New format: double-hyphen separator
  const doubleHyphenIndex = slugParam.indexOf('--');
  if (doubleHyphenIndex !== -1) {
    return slugParam.substring(0, doubleHyphenIndex);
  }

  // Old format fallback: first single hyphen
  const firstHyphenIndex = slugParam.indexOf('-');
  if (firstHyphenIndex === -1) {
    return slugParam;
  }

  return slugParam.substring(0, firstHyphenIndex);
}

/**
 * Generate full slug with ID and title
 * Format: [id]--[title-slug] (double hyphen separator)
 *
 * @param id - The music/song ID
 * @param title - The music/song title
 * @returns Full slug string
 */
export function generateFullSlug(id: string, title: string): string {
  if (!id) {
    return '';
  }

  const titleSlug = generateSlug(title);

  if (!titleSlug) {
    return id;
  }

  return `${id}--${titleSlug}`;
}

/**
 * Validate if a slug is in the correct format
 * Accepts both title slugs and full id--title slugs
 * @param slug - The slug to validate
 * @returns true if valid, false otherwise
 */
export function isValidSlug(slug: string): boolean {
  if (!slug || slug.trim() === '') {
    return false;
  }

  // Check for invalid characters (allow double-hyphen as separator)
  const invalidChars = /[^a-z0-9\-\u4e00-\u9fa5]/;
  if (invalidChars.test(slug)) {
    return false;
  }

  // Check if starts or ends with hyphen
  if (slug.startsWith('-') || slug.endsWith('-')) {
    return false;
  }

  return true;
}
